import React, { useState } from "react"
import PropTypes from "prop-types"
import MomentPropTypes from "react-moment-proptypes"
import DataTable, { tooltipHeaderRenderer } from "@swipesense/data-table"
import { Column, SortDirection } from "react-virtualized"
import CSVLink from "../CSVLink"
import { entitiesPropTypes } from "../../utils/propTypes"
import { basicSort } from "../../utils/sort"
import { ComplianceCircle } from "@swipesense/compliance-helpers"
import Loading from "../Loading"
import "./ExposedEmployeeList.scss"

const ExposedEmployeeList = (props) => {
  const dateFormat = "M/D/YYYY • h:mma"

  const [sortBy, setSortBy] = useState("exposureDuration")
  const [sortDirection, setSortDirection] = useState(SortDirection.DESC)
  const [rowClickedProps, setRowClickedProps] = useState({})
  const sortedData = props.rows.sort(basicSort(sortBy, sortDirection))

  const noRowsMessage =
    "No exposed employees detected. Please adjust your case time range to rerun the search."

  const clickRow = ({ rowData }) => {
    setRowClickedProps({
      name: rowData.name,
      employeeId: rowData.employeeId,
      role: rowData.role,
      department: rowData.department,
      compliance: rowData.compliance,
      entities: props.entities,
      userId: rowData.id,
      startAt: props.startAt,
      endAt: props.endAt,
    })
  }

  const csvHeaders = [
    { label: "Exposed Employee", key: "name" },
    { label: "Role", key: "role" },
    { label: "First Contact", key: "firstContact" },
    { label: "Last Contact", key: "lastContact" },
    { label: "Exposure Duration", key: "exposureDuration" },
    { label: "Department", key: "department" },
  ]

  const csvDataMap = (employee) => ({
    name: employee.name,
    role: employee.role,
    firstContact: employee.firstContact.format(dateFormat),
    lastContact: employee.lastContact.format(dateFormat),
    exposureDuration: employee.exposureDuration.humanize(),
    department: employee.department,
  })

  return (
    <div className="ExposedEmployeeList">
      <CSVLink
        data={props.rows}
        filename={`${props.caseId}_employee_list.csv`}
        headers={csvHeaders}
        mapData={csvDataMap}
      />
      <div className="wrapper">
        <DataTable
          data={sortedData}
          rowHeight={52}
          loading={props.loading}
          loadingComponent={<Loading />}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={(by, dir) => {
            setSortBy(by)
            setSortDirection(dir)
          }}
          onRowClick={clickRow}
          noRowsMessage={noRowsMessage}
        >
          {[
            <Column
              label="Exposed Employees"
              key="user"
              dataKey="user"
              disableSort={true}
              width={0}
              cellRenderer={({ rowData }) => (
                <div className="exposed-employees">
                  <div className="name">{rowData.name}</div>
                  <div className="role">{rowData.role}</div>
                </div>
              )}
            />,
            <Column
              label={
                props.type === "patientPositive"
                  ? "First Visit"
                  : "First Contact"
              }
              key="firstContact"
              dataKey="firstContact"
              width={0}
              cellRenderer={({ cellData }) => cellData.format(dateFormat)}
            />,
            <Column
              label={
                props.type === "patientPositive" ? "Last Visit" : "Last Contact"
              }
              key="lastContact"
              dataKey="lastContact"
              width={0}
              cellRenderer={({ cellData }) => cellData.format(dateFormat)}
            />,
            <Column
              label="Exposure Duration"
              key="exposureDuration"
              dataKey="exposureDuration"
              width={0}
              headerRenderer={tooltipHeaderRenderer(
                '"Exposed" indicates a visit to a room occupied by a contagious patient or employee.'
              )}
              cellRenderer={({ cellData }) => cellData.humanize()}
            />,
            <Column
              label="Department"
              key="department"
              dataKey="department"
              disableSort={true}
              width={0}
            />,
            <Column
              label="Compliance"
              key="compliance"
              dataKey="compliance"
              width={0}
              headerRenderer={tooltipHeaderRenderer(
                "User's hand hygiene compliance during the entire exposure period."
              )}
              cellRenderer={({ cellData }) =>
                cellData && (
                  <div className="room-compliance">
                    <ComplianceCircle compliance={cellData} />
                    {cellData.toFixed(0)}%
                  </div>
                )
              }
            />,
          ]
            .slice(0, props.type === "patientPositive" ? 6 : 5)
            .concat([
              <Column
                label=""
                key=""
                dataKey=""
                disableSort={true}
                maxWidth={10}
              />,
            ])}
        </DataTable>
      </div>
      <div className="total-results">
        {props.rows && (
          <div>
            {props.rows.length} {props.rows.length === 1 ? "result" : "results"}
          </div>
        )}
      </div>
      {React.createElement(props.onRowClickComponent, rowClickedProps)}
    </div>
  )
}

ExposedEmployeeList.propTypes = {
  loading: PropTypes.bool,
  type: PropTypes.oneOf(["employeePositive", "patientPositive"]).isRequired,
  startAt: PropTypes.string,
  endAt: PropTypes.string,
  caseId: PropTypes.string,
  entities: entitiesPropTypes,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      employeeId: PropTypes.string,
      role: PropTypes.string.isRequired,
      department: PropTypes.string.isRequired,
      exposureDuration: MomentPropTypes.momentDurationObj.isRequired,
      firstContact: MomentPropTypes.momentObj.isRequired,
      lastContact: MomentPropTypes.momentObj.isRequired,
      compliance: PropTypes.float,
    }).isRequired
  ),
  onRowClickComponent: PropTypes.func.isRequired,
}

ExposedEmployeeList.defaultProps = {
  rows: [],
  exposed: "employeePositive",
}

export default ExposedEmployeeList
